import { useEffect, useRef } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-contrast-drop-2-line',
    label: '경화 후 수축',
    desc: '실리콘이 굳으면서 부피가 줄어들어 틈이 발생',
  },
  {
    number: '02',
    icon: 'ri-scissors-cut-line',
    label: '균열 진행',
    desc: '온도 변화로 인해 균열이 점점 확대됨',
  },
  {
    number: '03',
    icon: 'ri-delete-bin-3-line',
    label: '이물질 축적',
    desc: '벌어진 틈에 먼지·이물질이 쌓여 누수 발생',
  },
];

const FieldProblem01Section = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef.current?.querySelectorAll('.anim');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 bg-[#F5F5F5]" ref={sectionRef}>
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">

          {/* 왼쪽: 텍스트 */}
          <div className="flex-1 anim opacity-0 translate-y-6 transition-all duration-700">
            <span className="inline-flex items-center gap-2 bg-red-100 text-red-600 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full border border-red-200 mb-6">
              <i className="ri-error-warning-fill" />
              FIELD PROBLEM 01
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1B2A4A] leading-tight mb-4 font-display">
              실리콘 균열 및<br />이물질 축적
            </h2>
            <p className="text-[#E63946] font-semibold text-base mb-5">
              기존 실리콘 도포 작업 방식의 구조적 한계
            </p>
            <p className="text-[#555] text-base leading-relaxed max-w-md">
              실리콘이 경화 후 수축·균열되어 틈이 벌어지고,
              이물질이 끼어 <strong className="text-[#1B2A4A]">방수 기능이 상실</strong>됩니다.
            </p>

            {/* 빨간 경고 배너 */}
            <div className="mt-10 flex items-center gap-3 bg-[#E63946] text-white rounded-xl px-6 py-4">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-alarm-warning-fill text-xl" />
              </div>
              <p className="font-semibold text-sm md:text-base">
                실리콘은 시간이 지날수록 <strong>반드시 열화됩니다</strong>
              </p>
            </div>
          </div>

          {/* 오른쪽: 세로 3단계 */}
          <div className="flex-1 flex flex-col gap-0 anim opacity-0 translate-y-6 transition-all duration-700 delay-150">
            {steps.map((step, i) => (
              <div key={i} className="flex gap-5 items-start">
                {/* 스텝 인디케이터 */}
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#E63946] text-white font-extrabold text-sm flex-shrink-0">
                    {step.number}
                  </div>
                  {i < steps.length - 1 && (
                    <div className="w-0.5 h-10 bg-[#E63946]/30 my-1" />
                  )}
                </div>
                {/* 콘텐츠 */}
                <div className="pb-8">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className={`${step.icon} text-[#E63946] text-lg`} />
                    </div>
                    <span className="text-[#1B2A4A] font-bold text-base">{step.label}</span>
                  </div>
                  <p className="text-[#666] text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

export default FieldProblem01Section;
